/**
 * ProfitRoom — Sprint 2 (2026-05-10)
 *
 * Centralny ekran modułu MASKIL Bid Profit Engine.
 * Eksponuje 22 endpointy z suppliers_router.py (po fixach Sprint 1).
 *
 * 5 sekcji:
 *  1. BoQ — wyciągnij z SWZ, edytuj, przegląd 10+ items z parametrami technicznymi
 *  2. Suppliers — auto-select 5-12 dostawców per kategoria z suppliers_kb (70 entries)
 *  3. RFQ Workflow — generuj artifacts (email + LinkedIn + voice script), approve, send
 *  4. Quote Comparison — 3-4 dostawców side-by-side, pick winners per BoQ item
 *  5. Verdict — profitability score, EV sensitivity, bid_mode recommendation
 *
 * Dziś (skeleton): Sekcja 1 LIVE (BoQ extract + tabela), reszta placeholders.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Calculator, Wand2, Users, Send, BarChart3, FileText,
  CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw, Download,
  Building2, Mail, Phone, MapPin, Calendar, TrendingUp, Sparkles,
  Briefcase, Linkedin, Phone as PhoneIcon, FileText as FileTextIcon,
  Eye, Check, X as XIcon, ExternalLink,
} from 'lucide-react';
import api, {
  getTenderDetail,
  extractTenderBoq, getTenderBoq,
  selectSuppliersForTender, generateTenderRfq,
  getTenderProfitability, getTenderBidMix,
  listRfqThreads, getRfqThread, approveRfqThread, sendRfqThread,
  patchBoqItem, setTenderBidMode, suggestBidMode,
} from '../lib/api';
import { markMissionComplete, MISSIONS } from '../lib/tutorialActions';
import { useAgentConfigGate } from '../components/common/JITAgentConfigModal';
import Tour, { shouldShowTour } from '../components/tutorial/Tour';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

// ====== Helper components ======

function VerdictBadge({ verdict, score }) {
  if (!verdict) return null;
  const map = {
    'recommended': { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', icon: CheckCircle2, text: 'Polecane' },
    'marginal':    { color: 'bg-amber-500/15 text-amber-300 border-amber-500/40',     icon: AlertTriangle, text: 'Marginalne' },
    'unprofitable': { color: 'bg-red-500/15 text-red-300 border-red-500/40',           icon: XCircle, text: 'Nieopłacalne' },
  };
  const cfg = map[verdict] || { color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40', icon: AlertTriangle, text: verdict };
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} text-sm px-3 py-1`}>
      <Icon className="w-4 h-4 mr-1.5" />
      {cfg.text} {score != null && `· ${Math.round(score)}/100`}
    </Badge>
  );
}

function BidModeBadge({ verdict, coverage }) {
  // Heurystyka rekomendacji bid_mode na bazie verdict + coverage
  // (W Sprint 3 zostanie zastąpiona przez dedicated /bid-mode endpoint)
  let mode = 'WATCH', label = 'Obserwuj', color = 'bg-zinc-500/15 text-zinc-300';
  if (verdict === 'recommended' && coverage >= 70) {
    mode = 'BID_AS_LEAD'; label = 'Startuj jako lead'; color = 'bg-emerald-500/20 text-emerald-300';
  } else if (verdict === 'recommended' && coverage < 70) {
    mode = 'BID_AS_LEAD?'; label = 'Sprawdź pokrycie'; color = 'bg-emerald-500/10 text-emerald-400';
  } else if (verdict === 'marginal') {
    mode = 'BID_AS_SUBCONTRACTOR'; label = 'Rozważ konsorcjum'; color = 'bg-amber-500/15 text-amber-300';
  } else if (verdict === 'unprofitable') {
    mode = 'PASS'; label = 'Odpuść'; color = 'bg-red-500/15 text-red-300';
  }
  return (
    <Badge className={`${color} text-xs`}>
      <Briefcase className="w-3 h-3 mr-1" /> {label}
    </Badge>
  );
}

function fmtPLN(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(n) + ' PLN';
}

// ====== Sekcja 1: BoQ ======

// Inline editable cell — click → input → blur saves
function EditableCell({ value, onSave, type = 'text', className = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setVal(value); }, [value]);

  const commit = async () => {
    if (val === value || val === '') {
      setEditing(false);
      setVal(value);
      return;
    }
    setSaving(true);
    try {
      await onSave(type === 'number' ? parseFloat(val) : val);
      setEditing(false);
    } catch (e) {
      setVal(value);
      setEditing(false);
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Input
        autoFocus
        type={type}
        value={val ?? ''}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setEditing(false); setVal(value); }
        }}
        className={`h-7 text-xs ${className}`}
        disabled={saving}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-muted/40 px-1 rounded ${className}`}
      title="Kliknij aby edytować"
    >
      {value ?? '—'}
      {saving && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
    </span>
  );
}

function SectionBoQ({ tenderId, boq, loading, onRefresh, onExtract, extracting, onItemPatched }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            1. Bill of Quantities (BoQ)
            {boq && <Badge variant="outline" className="text-xs">{boq.total_items || boq.items?.length || 0} pozycji</Badge>}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Odśwież
            </Button>
            <Button size="sm" onClick={onExtract} disabled={extracting}>
              {extracting
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Wand2 className="w-3.5 h-3.5 mr-1" />}
              {boq?.items?.length ? 'Re-extract z SWZ' : 'Wyciągnij z SWZ (LLM)'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : !boq || !boq.items?.length ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Brak BoQ. Kliknij <strong>"Wyciągnij z SWZ"</strong> żeby uruchomić LLM extraction.</p>
            <p className="text-xs mt-2">
              Wymaga pobranego SWZ (Załączniki → Pobierz). Dla TED z linkiem do e-Zamówienia: ręczne pobranie.
            </p>
          </div>
        ) : (
          <>
            {boq.summary?.total_estimated_value_pln && (
              <div className="text-xs text-muted-foreground mb-3">
                Szacowana wartość kontraktu: <strong>{fmtPLN(boq.summary.total_estimated_value_pln)}</strong>
                {boq.summary.tender_type && ` · typ: ${boq.summary.tender_type}`}
                {boq.extraction_quality?.completeness != null && (
                  <> · jakość extrakcji: {Math.round(boq.extraction_quality.completeness * 100)}%</>
                )}
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead className="w-[42%]">Pozycja</TableHead>
                  <TableHead>Ilość</TableHead>
                  <TableHead>Specyfikacja</TableHead>
                  <TableHead>CPV</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {boq.items.map((it) => (
                  <TableRow key={it.item_id || it.line_number}>
                    <TableCell className="text-xs text-muted-foreground">{it.line_number || '-'}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {it.item_id ? (
                          <EditableCell
                            value={it.description}
                            onSave={async (v) => {
                              await patchBoqItem(tenderId, it.item_id, { description: v });
                              if (onItemPatched) onItemPatched();
                            }}
                          />
                        ) : (
                          it.description
                        )}
                      </div>
                      {it.notes && <div className="text-xs text-muted-foreground mt-0.5">{it.notes}</div>}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {it.item_id ? (
                        <EditableCell
                          value={it.quantity}
                          type="number"
                          onSave={async (v) => {
                            await patchBoqItem(tenderId, it.item_id, { quantity: v });
                            if (onItemPatched) onItemPatched();
                          }}
                        />
                      ) : it.quantity}
                      {' '}{it.unit}
                    </TableCell>
                    <TableCell className="text-xs">
                      {it.specs ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(it.specs).slice(0, 4).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-[10px]">
                              {k}={String(v)}
                            </Badge>
                          ))}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {(it.cpv_codes || []).map(c => (
                        <Badge key={c} variant="outline" className="text-[10px] mr-1">{c}</Badge>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {boq.extraction_quality?.notes && (
              <div className="text-xs text-muted-foreground italic mt-3 border-t pt-2">
                💡 {boq.extraction_quality.notes}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ====== Sekcja 2: Suppliers ======

function SectionSuppliers({ suppliersData, loading, onSelectSuppliers, selecting }) {
  const total = suppliersData?.total_suppliers || 0;
  const cats = suppliersData?.by_category || {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            2. Dostawcy (auto-select z SKB)
            {total > 0 && <Badge variant="outline" className="text-xs">{total} kandydatów</Badge>}
          </CardTitle>
          <Button size="sm" onClick={onSelectSuppliers} disabled={selecting}>
            {selecting
              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Auto-select
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : total === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Brak dostawców. Kliknij <strong>"Auto-select"</strong> aby dopasować do BoQ.</p>
            <p className="text-xs mt-1">Wymaga: BoQ z kategoriami (sekcja 1).</p>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(cats).map(([cat, list]) => (
              <div key={cat}>
                <div className="text-xs font-medium text-muted-foreground uppercase mb-1.5 flex items-center gap-2">
                  {cat} <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {list.slice(0, 4).map(s => (
                    <Card key={s.supplier_id} className="hover:border-primary/40 transition">
                      <CardContent className="pt-3 pb-3 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium truncate">{s.name}</div>
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {s.tier_label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-0.5">
                            <MapPin className="w-3 h-3" /> {s.country}{s.city ? `, ${s.city}` : ''}
                          </span>
                          <span>trust {Math.round(s.trust_score)}</span>
                          <span className="text-emerald-400">match {Math.round(s.match_score)}</span>
                        </div>
                        {s.brands && s.brands.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {s.brands.slice(0, 3).map(b => (
                              <Badge key={b} variant="outline" className="text-[9px]">{b}</Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ====== Sekcja 3: RFQ Workflow z approval modal (Sprint 2 day 2) ======

const STATUS_BADGE_CONFIG = {
  drafted:   { color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40', label: 'Draft' },
  approved:  { color: 'bg-blue-500/15 text-blue-300 border-blue-500/40', label: 'Zatwierdzona' },
  sent:      { color: 'bg-amber-500/15 text-amber-300 border-amber-500/40', label: 'Wysłana' },
  received:  { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', label: 'Odpowiedź' },
  failed:    { color: 'bg-red-500/15 text-red-300 border-red-500/40', label: 'Błąd' },
};

function ThreadStatusBadge({ status }) {
  const cfg = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.drafted;
  return <Badge className={`${cfg.color} text-[10px]`}>{cfg.label}</Badge>;
}

function RfqApprovalModal({ thread, open, onClose, onApproved, onSent }) {
  const [tab, setTab] = useState('email');
  const [busy, setBusy] = useState(null);

  // T11 2026-05-27 — JIT email config gate. Gdy agent.identity.email == ""
  // (puste, default po sensible-defaults), pierwsze kliknięcie "Wyślij" otwiera
  // modal "Adam potrzebuje adresu email", po wypełnieniu kontynuuje send.
  const { needsConfig, openConfig, ConfigModal } = useAgentConfigGate('email');

  if (!thread) return null;

  const artifact = thread.rfq_artifact || {};
  // BUG #5 fix (2026-05-11): use supplier_name (from list join) — fallback _supplier_name (z modal load)
  const supplier = thread.supplier_name || thread._supplier_name || `Supplier ${(thread.supplier_id || '').slice(0, 8)}`;

  const handleApprove = async () => {
    setBusy('approve');
    try {
      await approveRfqThread(thread.id, true, null);
      toast.success('Zatwierdzono — gotowe do wysłania');
      if (onApproved) onApproved();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  // T11: opakowane handleSend → najpierw gate, potem actuallySend
  const actuallySend = async () => {
    setBusy('send');
    try {
      const r = await sendRfqThread(thread.id);
      // Backend zawsze zwraca 200 OK, ale flaga r.sent mówi co się rzeczywiście stało
      if (r.sent) {
        const mode = r.dry_run ? 'dry-run' : 'live';
        const source = r.resolved_source ? ` (źródło: ${r.resolved_source})` : '';
        toast.success(`Wysłano RFQ (${mode}) → ${r.to || 'email'}${source}`);
        markMissionComplete(MISSIONS.SEND_RFQ);
      } else if (r.suggested_action === 'add_supplier_contact' && r.supplier_id) {
        // Specjalny toast z akcją: prowadzi usera do dodania opiekuna
        toast.error(r.error || 'Brak adresu email', {
          duration: 10000,
          action: {
            label: 'Dodaj opiekuna →',
            onClick: () => {
              window.location.href = `/suppliers?contacts=${r.supplier_id}`;
            },
          },
        });
      } else {
        toast.error(`Wysyłka nie powiodła się: ${r.error || 'nieznany błąd'}`, {
          duration: 6000,
        });
      }
      if (onSent) onSent();
    } catch (e) {
      toast.error('Błąd wysyłki: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  // T11 — handler z JIT gate. Jeśli email agenta nieustawiony → openConfig (modal),
  // po wypełnieniu kontynuuje wysyłkę. Jeśli ustawiony → direct send.
  const handleSend = () => {
    if (needsConfig) {
      openConfig(() => actuallySend());
      return;
    }
    actuallySend();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4" /> RFQ — {supplier}
            <ThreadStatusBadge status={thread.status} />
          </DialogTitle>
          <DialogDescription>
            Podgląd wszystkich kanałów. Zatwierdź → wyślij. Możesz wysłać tylko email (reszta dry-run).
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="email">
              <Mail className="w-3.5 h-3.5 mr-1" /> Email
            </TabsTrigger>
            <TabsTrigger value="linkedin" disabled={!artifact.linkedin}>
              <Linkedin className="w-3.5 h-3.5 mr-1" /> LinkedIn
            </TabsTrigger>
            <TabsTrigger value="voice" disabled={!artifact.voice_script}>
              <PhoneIcon className="w-3.5 h-3.5 mr-1" /> Voice
            </TabsTrigger>
            <TabsTrigger value="pdf">
              <FileTextIcon className="w-3.5 h-3.5 mr-1" /> PDF
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-2">
            {artifact.email ? (
              <>
                <div className="text-xs">
                  <span className="text-muted-foreground">Do:</span>{' '}
                  <strong>{artifact.email.to || '—'}</strong>
                </div>
                <div className="text-xs">
                  <span className="text-muted-foreground">Temat:</span>{' '}
                  {artifact.email.subject}
                </div>
                <pre className="bg-muted/30 p-3 rounded text-xs whitespace-pre-wrap font-mono max-h-[400px] overflow-y-auto">
                  {artifact.email.body_text || ''}
                </pre>
              </>
            ) : <p className="text-sm text-muted-foreground">Brak email artifact.</p>}
          </TabsContent>

          <TabsContent value="linkedin">
            {artifact.linkedin ? (
              <>
                {artifact.linkedin.linkedin_url && (
                  <div className="text-xs">
                    <a href={artifact.linkedin.linkedin_url} target="_blank" rel="noreferrer"
                       className="text-primary flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {artifact.linkedin.linkedin_url}
                    </a>
                  </div>
                )}
                <pre className="bg-muted/30 p-3 rounded text-xs whitespace-pre-wrap mt-2">
                  {artifact.linkedin.message || ''}
                </pre>
              </>
            ) : <p className="text-sm text-muted-foreground">Brak LinkedIn artifact.</p>}
          </TabsContent>

          <TabsContent value="voice">
            {artifact.voice_script ? (
              <pre className="bg-muted/30 p-3 rounded text-xs whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                {artifact.voice_script.script_text || ''}
              </pre>
            ) : <p className="text-sm text-muted-foreground">Brak voice script.</p>}
          </TabsContent>

          <TabsContent value="pdf">
            <div className="text-sm space-y-2">
              <p>RFQ PDF generowany on-demand:</p>
              <a
                href={`${api.defaults.baseURL}/rfq/threads/${thread.id}/pdf?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                target="_blank" rel="noreferrer"
              >
                <Button variant="outline" size="sm">
                  <Download className="w-3.5 h-3.5 mr-1" /> Pobierz PDF
                </Button>
              </a>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Zamknij</Button>
          {thread.status === 'drafted' && (
            <Button onClick={handleApprove} disabled={busy === 'approve'}>
              {busy === 'approve' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Check className="w-3.5 h-3.5 mr-1" />}
              Zatwierdź
            </Button>
          )}
          {thread.status === 'approved' && (
            <Button onClick={handleSend} disabled={busy === 'send'}
              className="bg-emerald-600 hover:bg-emerald-700">
              {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Send className="w-3.5 h-3.5 mr-1" />}
              Wyślij
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {/* T11 — JIT email config modal: pokazuje się gdy needsConfig, wraca callback po zapisie */}
      <ConfigModal />
    </Dialog>
  );
}

function SectionRfqWorkflow({ tenderId, onGenerate, generating, lastResult, onRefreshAll }) {
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const r = await listRfqThreads({ opportunity_id: tenderId, limit: 50 });
      setThreads(r.items || []);
    } catch (e) {
      // ignore
    } finally {
      setLoadingThreads(false);
    }
  }, [tenderId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (lastResult) refresh(); }, [lastResult, refresh]);

  const grouped = {
    drafted:  threads.filter(t => t.status === 'drafted'),
    approved: threads.filter(t => t.status === 'approved'),
    sent:     threads.filter(t => t.status === 'sent'),
    received: threads.filter(t => t.status === 'received'),
  };

  const openModal = async (thread) => {
    // Load full thread with artifact
    // Backend returns {thread, supplier, messages, quotes} — flatten for modal
    try {
      const full = await getRfqThread(thread.id);
      const t = full.thread || full;
      setSelectedThread({
        ...t,
        _supplier_name: full.supplier?.name || full.supplier?.legal_name,
        _supplier_email: full.supplier?.main_email,
      });
      setModalOpen(true);
    } catch (e) {
      toast.error('Nie można załadować threadu: ' + e.message);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4 text-primary" />
            3. RFQ Workflow
            <Badge variant="outline" className="text-xs">{threads.length} threadów</Badge>
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loadingThreads}>
              <RefreshCw className={`w-3.5 h-3.5 ${loadingThreads ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={onGenerate} disabled={generating}>
              {generating
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Wand2 className="w-3.5 h-3.5 mr-1" />}
              Wygeneruj RFQ
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {threads.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Send className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Brak RFQ. Kliknij <strong>"Wygeneruj RFQ"</strong>.</p>
            <p className="text-xs mt-1">Wymaga BoQ + suppliers (sekcje 1-2).</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['drafted', 'approved', 'sent', 'received'].map(status => (
              <div key={status} className="space-y-2">
                <div className="text-xs font-medium uppercase text-muted-foreground flex items-center justify-between">
                  <span>{STATUS_BADGE_CONFIG[status].label}</span>
                  <Badge variant="outline" className="text-[10px]">{grouped[status].length}</Badge>
                </div>
                <div className="space-y-1.5">
                  {grouped[status].map(t => (
                    <Card key={t.id} className="cursor-pointer hover:border-primary/40 transition"
                          onClick={() => openModal(t)}>
                      <CardContent className="p-2 space-y-0.5">
                        <div className="text-xs font-medium truncate">
                          {t.supplier_name || t._supplier_name || `Supplier ${t.supplier_id?.slice(0, 8)}`}
                        </div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Mail className="w-2.5 h-2.5" />
                          {t.primary_channel || 'email'}
                          {t.rounds > 0 && <span> · runda {t.rounds}</span>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {grouped[status].length === 0 && (
                    <div className="text-[10px] text-muted-foreground text-center py-2 italic">—</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <RfqApprovalModal
          thread={selectedThread}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onApproved={() => { setModalOpen(false); refresh(); }}
          onSent={() => { setModalOpen(false); refresh(); if (onRefreshAll) onRefreshAll(); }}
        />
      </CardContent>
    </Card>
  );
}

function SectionQuoteComparison({ bidMix, loading, onCompute }) {
  const coveragePct = bidMix?.coverage_pct || 0;
  const isEstimate = bidMix?.mode === 'estimate';
  const mix = bidMix?.mix || {};
  const items = Object.entries(mix);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            4. Porównanie ofert (bid mix)
            {bidMix && (
              <Badge variant="outline" className="text-xs">
                pokrycie {Math.round(coveragePct)}%
              </Badge>
            )}
            {isEstimate && (
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px]">
                🟡 ESTIMATE
              </Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={onCompute} disabled={loading}>
            <Calculator className="w-3.5 h-3.5 mr-1" /> Recompute
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : !bidMix || (coveragePct === 0 && items.length === 0) ? (
          <div className="text-center py-6 text-muted-foreground">
            <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Brak ofert od dostawców. Wyślij RFQ (sekcja 3).</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Pokryte pozycje</div>
                <div className="font-semibold">
                  {bidMix.covered_items_count} / {bidMix.boq_items_count}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Suma kosztów netto</div>
                <div className="font-semibold">{fmtPLN(bidMix.total_net_pln)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Strategia</div>
                <div className="font-semibold text-xs">
                  {bidMix.estimate_strategy || 'real_quotes'}
                </div>
              </div>
            </div>
            <Progress value={coveragePct} className="h-2" />

            {bidMix.warning && (
              <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                ⚠ {bidMix.warning}
              </div>
            )}

            {items.length > 0 && (
              <div className="overflow-x-auto">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[35%]">Pozycja BoQ</TableHead>
                      <TableHead>Ilość</TableHead>
                      <TableHead>Wybrany dostawca</TableHead>
                      <TableHead>Cena jedn. (netto)</TableHead>
                      <TableHead>Suma</TableHead>
                      <TableHead>Lead time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.slice(0, 10).map(([itemId, m]) => {
                      const sup = m.selected_supplier || {};
                      return (
                        <TableRow key={itemId}>
                          <TableCell className="text-xs">
                            <div className="line-clamp-2">{m.boq_description}</div>
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {m.boq_quantity} {m.boq_unit}
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="font-medium">
                              {sup.supplier_name?.replace(/^\[(CATALOG|LLM_ESTIMATE)\] ?/, '') || '?'}
                            </div>
                            {sup.model_offered && (
                              <div className="text-[10px] text-muted-foreground line-clamp-1">
                                {sup.model_offered}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{fmtPLN(sup.unit_price_pln)}</TableCell>
                          <TableCell className="text-xs font-semibold">{fmtPLN(sup.total_pln)}</TableCell>
                          <TableCell className="text-xs">
                            {sup.delivery_days != null ? `${sup.delivery_days} dni` : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {items.length > 10 && (
                  <div className="text-[11px] text-muted-foreground text-center pt-2">
                    + {items.length - 10} więcej pozycji
                  </div>
                )}
              </div>
            )}

            {(bidMix.uncovered_items || []).length > 0 && (
              <div className="text-xs text-muted-foreground border-t pt-2">
                Niepokryte: <strong>{bidMix.uncovered_items.length}</strong> pozycji
                {' — '}
                {bidMix.uncovered_items.slice(0, 2).map(it => it.description?.substring(0, 30)).join(', ')}
                {bidMix.uncovered_items.length > 2 && '…'}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Sensitivity widget — interactive slider explores EV at different win-rates
function SensitivityWidget({ sensitivityTable, metrics }) {
  const points = sensitivityTable || [];

  // Compute initial winRate (must be done BEFORE useState — but no hook calls)
  const minWR = points.length > 0 ? Math.min(...points.map(p => p.win_rate_pct)) : 0;
  const maxWR = points.length > 0 ? Math.max(...points.map(p => p.win_rate_pct)) : 100;
  const initialWR = points.length > 0
    ? (points.find(p => p.win_rate_pct === metrics?.win_rate_pct) || points[Math.floor(points.length / 2)]).win_rate_pct
    : 30;

  // Hooks must always be called in same order — even when we return null below
  const [winRate, setWinRate] = useState(initialWR);

  if (points.length === 0) return null;

  // Interpolate EV at arbitrary win_rate using linear interp between table points
  const interpEV = (wr) => {
    // Find adjacent points
    const sorted = [...points].sort((a, b) => a.win_rate_pct - b.win_rate_pct);
    if (wr <= sorted[0].win_rate_pct) return sorted[0].ev_pln;
    if (wr >= sorted[sorted.length - 1].win_rate_pct) return sorted[sorted.length - 1].ev_pln;
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i], b = sorted[i + 1];
      if (wr >= a.win_rate_pct && wr <= b.win_rate_pct) {
        const t = (wr - a.win_rate_pct) / (b.win_rate_pct - a.win_rate_pct);
        return a.ev_pln + t * (b.ev_pln - a.ev_pln);
      }
    }
    return 0;
  };

  const currentEV = interpEV(winRate);
  const isPositive = currentEV > 0;

  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Sensitivity — przeciągnij suwak żeby zobaczyć EV przy innym win-rate:
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {fmtPLN(currentEV)}
          </div>
          <div className="text-[10px] text-muted-foreground">EV @ win={winRate}%</div>
        </div>
      </div>

      <Slider
        value={[winRate]}
        min={minWR}
        max={maxWR}
        step={1}
        onValueChange={(v) => setWinRate(v[0])}
        className="w-full"
      />

      {/* Bar chart of all points */}
      <div className="grid grid-cols-6 gap-1 text-[11px]">
        {points.map(row => {
          const isCurrent = Math.abs(row.win_rate_pct - winRate) <= 5;
          return (
            <div
              key={row.win_rate_pct}
              className={`text-center p-1 rounded transition ${
                row.is_positive ? 'bg-emerald-500/10' : 'bg-red-500/10'
              } ${isCurrent ? 'ring-2 ring-primary' : ''}`}
            >
              <div className="text-muted-foreground">{row.win_rate_pct}%</div>
              <div className={row.is_positive ? 'text-emerald-300' : 'text-red-300'}>
                {Math.round(row.ev_pln / 1000)}k
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-muted-foreground italic">
        💡 Cost of failure: {fmtPLN(metrics?.cost_of_failure_pln)} (czas + LLM + RFQ).
        EV jest dodatni gdy spodziewany zysk × win_rate {'>'} cost_of_failure.
      </div>
    </div>
  );
}

function SectionVerdict({ profitability, loading, onCompute, lastError, onRecomputeEstimate }) {
  const isEstimate = profitability?._estimate_mode;
  return (
    <Card className={profitability?.verdict === 'recommended' ? 'border-emerald-500/30' :
                     profitability?.verdict === 'unprofitable' ? 'border-red-500/30' :
                     isEstimate ? 'border-amber-500/30' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            5. Verdict — czy zarobimy?
            {isEstimate && (
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px]">
                🟡 ESTIMATE conf={Math.round((profitability._estimate_confidence || 0) * 100)}%
              </Badge>
            )}
          </CardTitle>
          <div className="flex gap-2 items-center">
            <VerdictBadge
              verdict={profitability?.verdict}
              score={profitability?.profitability_score}
            />
            <Button variant="outline" size="sm" onClick={onCompute} disabled={loading}>
              <Calculator className="w-3.5 h-3.5 mr-1" /> Przelicz (real)
            </Button>
            {onRecomputeEstimate && (
              <Button variant="outline" size="sm" onClick={onRecomputeEstimate} disabled={loading}>
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Estimate
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : !profitability ? (
          lastError ? (
            <div className="text-center py-6">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
              <p className="text-sm font-medium mb-1">Niemożliwa kalkulacja</p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">{lastError}</p>
              <p className="text-[11px] text-muted-foreground mt-3 italic">
                💡 Pobierz SWZ + extract BoQ → potem przelicz ponownie
              </p>
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Brak kalkulacji. Wymaga BoQ + supplier_quotes (lub estimate).</p>
            </div>
          )
        ) : (
          <div className="space-y-3">
            {/* T37 2026-05-29 — wyraźny disclaimer gdy verdict liczony na estymatach.
                User NIE może podejmować BID/PASS myśląc że to twarde liczby. */}
            {isEstimate && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-amber-200/90">
                  <strong>To są liczby SZACUNKOWE</strong> ({Math.round((profitability._estimate_confidence || 0) * 100)}% pewności).
                  Marża i zysk policzone na cenach estymowanych z parametrów produktów (power/lumeny), nie na realnych cenach
                  zakupu z Twojego cennika. <strong>Nie podejmuj decyzji BID/PASS wyłącznie na tej podstawie.</strong>
                  {' '}Aby uzyskać twarde liczby: wgraj cennik dystrybutora (cost_price) i kliknij „Przelicz (real)".
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Cena docelowa</div>
                <div className="font-semibold">{fmtPLN(profitability.metrics?.target_bid_pln)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Koszty zakupu</div>
                <div className="font-semibold">{fmtPLN(profitability.metrics?.total_cost_pln)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Marża %</div>
                <div className="font-semibold">{profitability.metrics?.target_margin_pct}%</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Zysk docelowy</div>
                <div className="font-semibold text-emerald-400">{fmtPLN(profitability.metrics?.target_profit_pln)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">EV @ win=30%</div>
                <div className={`font-semibold ${profitability.metrics?.ev_at_target_pln > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {fmtPLN(profitability.metrics?.ev_at_target_pln)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Próg rentowności</div>
                <div className="font-semibold">{fmtPLN(profitability.metrics?.breakeven_bid_pln)}</div>
              </div>
            </div>
            {profitability.reasoning && profitability.reasoning.length > 0 && (
              <div className="border-l-2 border-primary/40 pl-3 py-1 text-sm">
                {profitability.reasoning.map((r, i) => (
                  <div key={i}>{r}</div>
                ))}
              </div>
            )}
            {profitability.sensitivity_table && (
              <SensitivityWidget
                sensitivityTable={profitability.sensitivity_table}
                metrics={profitability.metrics}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ====== Main ======

export default function ProfitRoom({ lang, tenantId }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [opp, setOpp] = useState(null);
  const [boq, setBoq] = useState(null);
  const [boqLoading, setBoqLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);

  const [suppliersData, setSuppliersData] = useState(null);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [rfqResult, setRfqResult] = useState(null);

  const [bidMix, setBidMix] = useState(null);
  const [bidMixLoading, setBidMixLoading] = useState(false);

  const [profitability, setProfitability] = useState(null);
  const [profitLoading, setProfitLoading] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const tid = setTimeout(() => {
      if (shouldShowTour('profit-room-overview')) setShowTour(true);
    }, 1600);
    return () => clearTimeout(tid);
  }, []);

  // Initial load
  const loadAll = useCallback(async () => {
    try {
      const detail = await getTenderDetail(id);
      setOpp(detail.opportunity);
    } catch (e) { /* ignore */ }
    setBoqLoading(true);
    try {
      const b = await getTenderBoq(id);
      setBoq(b);
    } catch (e) {
      // 404 = no BoQ yet — OK
      setBoq(null);
    } finally {
      setBoqLoading(false);
    }
    // try profitability/bid-mix silently — they may have been computed before
    try { setProfitability(await getTenderProfitability(id)); } catch (e) { /* */ }
    try { setBidMix(await getTenderBidMix(id)); } catch (e) { /* */ }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Actions
  const doExtract = async () => {
    setExtracting(true);
    try {
      const r = await extractTenderBoq(id);
      toast.success(`Wyciągnięto ${r.items_extracted} pozycji z SWZ`);
      markMissionComplete(MISSIONS.EXTRACT_BOQ);
      const b = await getTenderBoq(id);
      setBoq(b);
    } catch (e) {
      toast.error('Extract nie powiódł się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExtracting(false);
    }
  };

  const doSelectSuppliers = async () => {
    setSelecting(true);
    try {
      const r = await selectSuppliersForTender(id, { suppliers_per_category: 4 });
      setSuppliersData(r);
      toast.success(`Dopasowano ${r.total_suppliers} dostawców z ${r.categories_count} kategorii`);
      if ((r.total_suppliers || 0) >= 3) {
        markMissionComplete(MISSIONS.SELECT_SUPPLIERS);
      }
    } catch (e) {
      toast.error('Auto-select fail: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSelecting(false);
    }
  };

  const doGenerateRfq = async () => {
    setGenerating(true);
    try {
      const r = await generateTenderRfq(id, {
        operator_name: 'MASKIL Operator',
        response_deadline_days: 7,
        suppliers_per_category: 3,
      });
      setRfqResult(r);
      toast.success(`Wygenerowano ${r.threads_created} threadów RFQ (drafts)`);
    } catch (e) {
      toast.error('RFQ generate fail: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const [profitError, setProfitError] = useState(null);
  const [bidMode, setBidMode] = useState(null);
  const [bidSuggestion, setBidSuggestion] = useState(null);

  // Read bid_mode from opportunity (initially) + sync after change
  useEffect(() => {
    if (opp?.bid_mode) setBidMode(opp.bid_mode);
  }, [opp?.bid_mode]);

  // FIX #1 (2026-05-11): auto-suggest bid_mode po obliczeniu profitability
  useEffect(() => {
    if (!id || !profitability) return;
    suggestBidMode(id)
      .then(s => setBidSuggestion(s))
      .catch(() => setBidSuggestion(null));
  }, [id, profitability]);

  const handleBidModeChange = async (newMode) => {
    if (!newMode || newMode === bidMode) return;
    setBidMode(newMode);  // optimistic
    try {
      await setTenderBidMode(id, newMode);
      toast.success(`Bid mode: ${newMode}`);
      // Refresh opp to see persisted state
      try {
        const detail = await getTenderDetail(id);
        setOpp(detail.opportunity);
      } catch (e) { /* ignore */ }
    } catch (e) {
      setBidMode(opp?.bid_mode || null);  // rollback
      toast.error('Bid mode fail: ' + (e.response?.data?.detail || e.message));
    }
  };

  const doRecomputeBidMix = async (mode = 'auto') => {
    setBidMixLoading(true);
    try {
      const r = await api.post(`/tenders/${encodeURIComponent(id)}/bid-mix`,
        null, { params: { mode } }).then(rr => rr.data);
      setBidMix(r);
      if (r.error) {
        toast.error(r.error);
      } else {
        const note = r.mode === 'estimate' ? '🟡 estimate' : 'real quotes';
        toast.success(`Bid mix (${note}): pokrycie ${Math.round(r.coverage_pct || 0)}%`);
      }
    } catch (e) {
      toast.error('Bid mix fail: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBidMixLoading(false);
    }
  };

  const doRecomputeProfit = async (mode = 'auto') => {
    setProfitLoading(true);
    setProfitError(null);
    try {
      const r = await api.post(`/tenders/${encodeURIComponent(id)}/profitability`,
        null, { params: { mode } }).then(rr => rr.data);
      setProfitability(r);
      toast.success(`Verdict: ${r.verdict_label}`);
      markMissionComplete(MISSIONS.CHECK_VERDICT);
      // Quotes view = profitability też wymaga quote data, więc mark view_quotes
      markMissionComplete(MISSIONS.VIEW_QUOTES);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setProfitError(msg);
      setProfitability(null);
      toast.error('Profitability: ' + msg);
    } finally {
      setProfitLoading(false);
    }
  };

  const doRecomputeEstimate = () => doRecomputeProfit('estimate');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/tenders/${encodeURIComponent(id)}`)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Powrót
            </Button>
          </div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            Profit Room
          </h1>
          <p className="text-sm text-muted-foreground line-clamp-1">
            {opp?.title || 'Ładuję...'}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {opp?.source && <Badge variant="outline" className="text-[10px]">{opp.source.toUpperCase()}</Badge>}
            {opp?.city && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {opp.city}{opp.region ? `, ${opp.region}` : ''}
              </span>
            )}
            {opp?.deadline && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {opp.deadline.slice(0, 10)}
              </span>
            )}
            {opp?.estimated_value > 0 && (
              <span className="text-xs text-muted-foreground">{fmtPLN(opp.estimated_value)}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <VerdictBadge verdict={profitability?.verdict} score={profitability?.profitability_score} />

          {/* Bid mode dropdown — manual override */}
          <Select value={bidMode || ''} onValueChange={handleBidModeChange}>
            <SelectTrigger className="h-8 text-xs w-[220px]">
              <SelectValue placeholder="🎯 Wybierz strategię (bid mode)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BID_AS_LEAD">🟢 Bid as Lead — startujemy</SelectItem>
              <SelectItem value="BID_AS_SUBCONTRACTOR">🔵 Subcontractor — przez GW</SelectItem>
              <SelectItem value="REFER_TO_PARTNER">🟣 Refer — partnerowi za prowizję</SelectItem>
              <SelectItem value="WATCH_ONLY">🟡 Watch — obserwujemy konkurencję</SelectItem>
              <SelectItem value="PASS">🔴 Pass — odpuszczamy</SelectItem>
            </SelectContent>
          </Select>

          {/* AI suggestion — FIX #1 (2026-05-11) */}
          {bidSuggestion && bidSuggestion.recommended_mode !== bidMode && (
            <div className="text-[10px] bg-primary/10 border border-primary/30 rounded p-1.5 max-w-[220px]">
              <div className="flex items-center justify-between gap-1">
                <span className="text-primary">
                  💡 Adam: {bidSuggestion.recommended_mode.replace(/_/g, ' ').toLowerCase()}
                </span>
                <Badge variant="outline" className="text-[9px]">
                  {Math.round(bidSuggestion.confidence * 100)}%
                </Badge>
              </div>
              {bidSuggestion.reasoning && bidSuggestion.reasoning[0] && (
                <div className="text-muted-foreground mt-0.5 line-clamp-2">
                  {bidSuggestion.reasoning[0]}
                </div>
              )}
              <button
                className="text-[9px] text-primary underline mt-0.5"
                onClick={() => handleBidModeChange(bidSuggestion.recommended_mode)}
              >
                Zastosuj sugestię →
              </button>
            </div>
          )}

          {!bidMode && (
            <BidModeBadge
              verdict={profitability?.verdict}
              coverage={bidMix?.coverage_pct || 0}
            />
          )}
        </div>
      </div>

      {/* Sections */}
      <div data-tour="profit-boq">
        <SectionBoQ
          tenderId={id}
          boq={boq}
          loading={boqLoading}
          onRefresh={loadAll}
          onExtract={doExtract}
          onItemPatched={async () => {
            // IMPROV (2026-05-11): auto-refresh verdict + bid-mix po edit BoQ
            await loadAll();
            // Nie blokujemy UI — fire-and-forget recompute
            doRecomputeBidMix('auto').catch(() => {});
            doRecomputeProfit('auto').catch(() => {});
          }}
          extracting={extracting}
        />
      </div>

      <div data-tour="profit-suppliers">
        <SectionSuppliers
          suppliersData={suppliersData}
          loading={suppliersLoading}
          onSelectSuppliers={doSelectSuppliers}
          selecting={selecting}
        />
      </div>

      <div data-tour="profit-rfq">
        <SectionRfqWorkflow
          tenderId={id}
          onGenerate={doGenerateRfq}
          generating={generating}
          lastResult={rfqResult}
          onRefreshAll={() => doRecomputeBidMix('auto')}
        />
      </div>

      <div data-tour="profit-quotes">
        <SectionQuoteComparison
          bidMix={bidMix}
          loading={bidMixLoading}
          onCompute={() => doRecomputeBidMix('auto')}
        />
      </div>

      <div data-tour="profit-verdict">
        <SectionVerdict
          profitability={profitability}
          loading={profitLoading}
          onCompute={() => doRecomputeProfit('auto')}
          onRecomputeEstimate={doRecomputeEstimate}
          lastError={profitError}
        />
      </div>

      {/* Footer info */}
      <div className="text-[11px] text-muted-foreground text-center pt-2 pb-4">
        Sprint 2 dzień 2 — pełen workflow live: BoQ extract, Suppliers auto-select,
        RFQ Workflow (drafted→approved→sent→received) z 4-tab modal,
        Quote Comparison side-by-side, Profitability z mode=auto/actual/estimate.
      </div>

      <Tour
        tourId="profit-room-overview"
        lang={lang}
        active={showTour}
        onComplete={() => setShowTour(false)}
        onSkip={() => setShowTour(false)}
        steps={[
          {
            target: '[data-tour="profit-boq"]',
            title: lang === 'pl' ? 'Krok 1 — BoQ z SWZ' : 'Step 1 — BoQ from SWZ',
            content: lang === 'pl'
              ? 'Wyciągamy pozycje przedmiarowe z SWZ. Klik „Extract BoQ" — ADAM czyta PDF, ekstrahuje ilości, jednostki, opisy. Możesz edytować przed dalszymi krokami.'
              : 'We extract line items from SWZ. Click "Extract BoQ" — ADAM reads the PDF, pulls quantities, units, descriptions. Edit before continuing.',
            placement: 'bottom',
          },
          {
            target: '[data-tour="profit-suppliers"]',
            title: lang === 'pl' ? 'Krok 2 — Dopasuj dostawców' : 'Step 2 — Match suppliers',
            content: lang === 'pl'
              ? 'Auto-select znajduje 3-4 dostawców per kategoria BoQ (na podstawie produktów w bazie, geo, historii). Możesz odznaczać/dodawać ręcznie.'
              : 'Auto-select picks 3-4 suppliers per BoQ category (using product DB, geo, history). You can deselect/add manually.',
            placement: 'top',
          },
          {
            target: '[data-tour="profit-rfq"]',
            title: lang === 'pl' ? 'Krok 3 — RFQ do dostawców' : 'Step 3 — RFQ to suppliers',
            content: lang === 'pl'
              ? 'Generuj draft RFQ → zatwierdź → wyślij. Każdy wątek to oddzielny email z deadline. ADAM trzyma stan: drafted → approved → sent → received.'
              : 'Generate RFQ draft → approve → send. Each thread is a separate email with deadline. ADAM tracks: drafted → approved → sent → received.',
            placement: 'top',
          },
          {
            target: '[data-tour="profit-quotes"]',
            title: lang === 'pl' ? 'Krok 4 — Porównaj oferty' : 'Step 4 — Compare quotes',
            content: lang === 'pl'
              ? 'Side-by-side wszystkie odpowiedzi dostawców z najtańszą kolumną podświetloną. Coverage % = ile pozycji BoQ pokryliśmy real cenami (reszta to estymata).'
              : 'Side-by-side all supplier replies, cheapest column highlighted. Coverage % = how many BoQ items we cover with real prices (rest = estimate).',
            placement: 'top',
          },
          {
            target: '[data-tour="profit-verdict"]',
            title: lang === 'pl' ? 'Krok 5 — Werdykt GO/NO-GO' : 'Step 5 — GO/NO-GO verdict',
            content: lang === 'pl'
              ? 'Profitability score, margin, bid mode (LEAD/SUB/REFER/WATCH/PASS). Auto = real ceny, estimate = z bazy katalogowej. Tu zapadają decyzje strategiczne.'
              : 'Profitability score, margin, bid mode. Auto = real prices, estimate = catalog DB. Strategic decisions land here.',
            placement: 'top',
          },
        ]}
      />
    </div>
  );
}
